/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback, useState } from 'react';

/**
 * A form that shows the server's values until the user types, and their own from then on.
 *
 * Server data lands after the first paint and again after each save, so the form cannot simply be
 * initialised from it; equally, re-seeding unconditionally would wipe out whatever the user was
 * part-way through writing. Holding the edits separately and falling back to the seed says both in
 * one expression, and leaves nothing to keep in sync.
 */
export function useSeededForm<T extends Record<string, string>>(seed: T) {
  const [edits, setEdits] = useState<Partial<T> | null>(null);
  const change = useCallback(
    (key: keyof T) => (value: string) =>
      setEdits((prev) => ({ ...prev, [key]: value }) as Partial<T>),
    [],
  );
  /** Drops the edits, so the form falls back to whatever the seed says next. */
  const reset = useCallback(() => setEdits(null), []);
  return { values: { ...seed, ...edits }, change, reset, edited: edits !== null };
}
